import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import '../App.css';

export default function ViewReceipt() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const printRef = useRef();

  // Format currency in ZAR
  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2
    }).format(amount);
  }, []);

  // Format date
  const formatDate = useCallback((dateInput) => {
    if (!dateInput) return 'N/A';
    
    try {
      let date;
      if (typeof dateInput === 'string') {
        date = new Date(dateInput);
      } else if (dateInput.toDate) {
        date = dateInput.toDate();
      } else {
        date = new Date(dateInput);
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error, dateInput);
      return 'Invalid Date';
    }
  }, []);

  // Generate PDF preview
  const generatePdfPreview = useCallback((receiptData) => {
    if (!receiptData) return;
    
    try {
      // Use a web worker for PDF generation
      const worker = new Worker(new URL('../workers/pdfGenerator.js', import.meta.url));
      
      worker.onmessage = (e) => {
        if (e.data.type === 'preview') {
          setPdfPreview(e.data.preview);
        } else if (e.data.type === 'error') {
          console.error('PDF generation error:', e.data.error);
          setError('Error generating PDF preview. Please try again later.');
        }
      };
      
      worker.onerror = (error) => {
        console.error('Worker error:', error);
        setError('Error initializing PDF generator. Please try again later.');
      };
      
      // Create a deep copy of the receipt data to avoid issues with Firestore objects
      const safeReceiptData = JSON.parse(JSON.stringify(receiptData));
      
      // Ensure dates are properly formatted for the worker
      if (safeReceiptData.paymentDate) {
        safeReceiptData.paymentDateFormatted = formatDate(safeReceiptData.paymentDate);
      }
      
      if (safeReceiptData.createdAt) {
        safeReceiptData.createdAtFormatted = formatDate(safeReceiptData.createdAt);
      }
      
      worker.postMessage({
        action: 'generateReceiptPreview',
        receiptData: safeReceiptData
      });
      
      return () => {
        worker.terminate();
      };
    } catch (error) {
      console.error('Error setting up PDF worker:', error);
      setError('Failed to initialize PDF generator');
    }
  }, [formatDate]);

  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        const receiptRef = doc(db, 'receipts', id);
        const receiptSnap = await getDoc(receiptRef);
        
        if (receiptSnap.exists()) {
          const data = {
            id: receiptSnap.id,
            ...receiptSnap.data()
          };
          setReceipt(data);
          
          // Generate PDF preview after receipt is loaded
          if (data) {
            generatePdfPreview(data);
          }
        } else {
          setError('Receipt not found');
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching receipt:', error);
        setError('Error loading receipt');
        setLoading(false);
      }
    };
    
    fetchReceipt();
  }, [id, generatePdfPreview]);

  // Handle downloading the PDF
  const handleDownloadPDF = useCallback(() => {
    if (!receipt) return;
    
    // Show loading indicator
    setDownloading(true);
    
    try {
      // Use a web worker for PDF generation
      const worker = new Worker(new URL('../workers/pdfGenerator.js', import.meta.url));
      
      worker.onmessage = (e) => {
        if (e.data.type === 'download') {
          // Create a download link
          const link = document.createElement('a');
          link.href = e.data.pdfUrl;
          link.download = `receipt_${receipt.receiptNumber}_${Date.now()}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setDownloading(false);
        } else if (e.data.type === 'error') {
          console.error('PDF download error:', e.data.error);
          setError('Error generating PDF. Please try again later.');
          setDownloading(false);
        }
      };
      
      worker.onerror = (error) => {
        console.error('Worker error:', error);
        setError('Error initializing PDF generator. Please try again later.');
        setDownloading(false);
      };
      
      // Create a deep copy of the receipt data to avoid issues with Firestore objects
      const safeReceiptData = JSON.parse(JSON.stringify(receipt));
      
      // Ensure dates are properly formatted for the worker
      if (safeReceiptData.paymentDate) {
        safeReceiptData.paymentDateFormatted = formatDate(safeReceiptData.paymentDate);
      }
      
      if (safeReceiptData.createdAt) {
        safeReceiptData.createdAtFormatted = formatDate(safeReceiptData.createdAt);
      }
      
      worker.postMessage({
        action: 'generateReceiptDownload',
        receiptData: safeReceiptData
      });
    } catch (error) {
      console.error('Error setting up PDF worker:', error);
      setError('Failed to initialize PDF generator');
      setDownloading(false);
    }
  }, [receipt, formatDate]);

  if (loading) return (
    <div className="invoice-container">
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading receipt...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="invoice-container">
      <div className="error-container">
        <p className="error-message">{error}</p>
      </div>
    </div>
  );

  if (!receipt) return null;

  return (
    <div className="invoice-container">
      <div className="invoice-grid">
        <div className="invoice-details">
          <div className="invoice-card" ref={printRef}>
            {/* Receipt header with actions */}
            <div className="invoice-header">
              <div>
                <h1 className="invoice-title">Receipt #{receipt.receiptNumber}</h1>
                <p className="invoice-date">
                  {formatDate(receipt.paymentDate || receipt.createdAt?.toDate())}
                </p>
              </div>
              <div className="invoice-actions print:hidden">
                <button 
                  onClick={handleDownloadPDF}
                  className="btn btn-primary"
                  disabled={downloading}
                >
                  {downloading ? (
                    <>
                      <div className="loading-spinner"></div>
                      Downloading...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="btn-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download PDF
                    </>
                  )}
                </button>
                <button 
                  onClick={() => navigate('/receipts')}
                  className="btn btn-secondary"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="btn-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to Receipts
                </button>
              </div>
            </div>
            
            <div className="invoice-grid">
              {/* Company Information */}
              <div className="invoice-section">
                <h2 className="invoice-section-title">From</h2>
                <p className="font-semibold">{receipt.companyName || 'Your Company'}</p>
                <p>{receipt.companyAddress || 'Company Address'}</p>
                <p>{receipt.companyEmail || 'company@example.com'}</p>
                <p>{receipt.companyPhone || '(123) 456-7890'}</p>
              </div>
              
              {/* Client Information */}
              <div className="invoice-section">
                <h2 className="invoice-section-title">To</h2>
                <p className="font-semibold">{receipt.clientName}</p>
                <p>{receipt.clientAddress}</p>
                <p>{receipt.clientEmail}</p>
                <p>{receipt.clientPhone}</p>
              </div>
            </div>
            
            {/* Receipt Details */}
            <div className="invoice-details">
              <h2 className="invoice-section-title">Receipt Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="invoice-detail-row">
                    <span className="invoice-detail-label">Receipt Number:</span> 
                    {receipt.receiptNumber}
                  </p>
                  <p className="invoice-detail-row">
                    <span className="invoice-detail-label">Payment Date:</span> 
                    {formatDate(receipt.paymentDate)}
                  </p>
                  {receipt.invoiceNumber && (
                    <p className="invoice-detail-row">
                      <span className="invoice-detail-label">Invoice Reference:</span> 
                      {receipt.invoiceNumber}
                    </p>
                  )}
                  <p className="invoice-detail-row">
                    <span className="invoice-detail-label">Payment Method:</span> 
                    {receipt.paymentMethod === 'cash' ? 'Cash' : 
                     receipt.paymentMethod === 'bank_transfer' ? 'Bank Transfer' :
                     receipt.paymentMethod === 'credit_card' ? 'Credit Card' :
                     receipt.paymentMethod === 'check' ? 'Check' : 'Other'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Payment Amount */}
            <div className="invoice-summary">
              <div className="invoice-summary-row">
                <span className="invoice-summary-label">Amount Paid:</span>
                <span className="invoice-summary-value">{formatCurrency(receipt.amount)}</span>
              </div>
              
              {receipt.isPartialPayment && (
                <>
                  <div className="invoice-summary-row">
                    <span className="invoice-summary-label">Total Invoice Amount:</span>
                    <span className="invoice-summary-value">{formatCurrency(receipt.totalInvoiceAmount)}</span>
                  </div>
                  <div className="invoice-summary-row font-bold">
                    <span className="invoice-summary-label">Amount Due:</span>
                    <span className="invoice-summary-value text-red-600">{formatCurrency(receipt.remainingBalance)}</span>
                  </div>
                </>
              )}
            </div>
            
            {/* Notes */}
            {receipt.notes && (
              <div className="invoice-notes">
                <h2 className="invoice-section-title">Notes</h2>
                <p>{receipt.notes}</p>
              </div>
            )}
            
            <div className="text-center text-gray-500 mt-8 print:mt-16">
              Thank you for your business!
            </div>
          </div>
        </div>
        
        {pdfPreview && (
          <div className="pdf-preview-container">
            <h2 className="pdf-preview-title">PDF Preview</h2>
            <iframe
              src={pdfPreview}
              title="PDF Preview"
              width="100%"
              height="600px"
              frameBorder="0"
            ></iframe>
          </div>
        )}
      </div>
    </div>
  );
}
